<template>
  <div
    class="control-bar-display"
    :class="rootClass"
  >
    <!-- 信息显示屏：与 git 原版 scope 区 DOM / class 完全一致 -->
    <div
      v-if="showInfoPart"
      class="scrollControlBar__summaryScope scrollControlBar__summaryScope--interactive"
    >
      <control-bar-carousel
        v-if="!hasActiveScopeFilters && useBroadcastCarousel"
        :slides="broadcastSlides"
        :pause-on-hover="broadcastPauseOnHover"
      />
      <span
        v-else-if="!hasActiveScopeFilters"
        class="scrollControlBar__scopeEmpty"
      >
        {{ scopeDefaultLabel }}
      </span>
      <el-tooltip
        v-else
        effect="dark"
        placement="bottom"
        popper-class="genericResourceTooltip"
        :content="scopeFilterTooltip"
      >
        <div class="scrollControlBar__scopeChipsWrap">
          <div
            class="scrollControlBar__scopeChips"
            :class="scopeChipsCountClass(scopeFilters.length)"
          >
            <button
              v-for="chip in scopeFilters"
              :key="chip.key"
              type="button"
              class="scrollControlBar__scopeChip"
              @click="$emit('open-filter')"
            >
              <span class="scrollControlBar__scopeChipLabel">{{ chip.label }}</span>
              <span class="scrollControlBar__scopeChipValue">{{ chip.value }}</span>
            </button>
          </div>
        </div>
      </el-tooltip>
    </div>

    <!-- 操作显示屏：复用 scopeChips 容器与 scopeChip 格，与三维筛选同款 UI -->
    <div
      v-if="showActionsPart"
      class="scrollControlBar__summaryScope scrollControlBar__summaryScope--interactive"
    >
      <div class="scrollControlBar__scopeChipsWrap">
        <div class="scrollControlBar__scopeChips scrollControlBar__scopeChips--count-3">
          <button
            type="button"
            class="scrollControlBar__scopeChip scrollControlBar__scopeChip--batchTag scrollControlBar__scopeChip--emphasis"
            @click.stop="$emit('batch-tag')"
          >
            <span class="scrollControlBar__scopeChipLabel">批量打标</span>
            <span class="scrollControlBar__scopeChipValue">({{ selectionCount }})</span>
          </button>
          <button
            type="button"
            class="scrollControlBar__scopeChip scrollControlBar__scopeChip--export"
            @click.stop="$emit('export-selected')"
          >
            导出所选
          </button>
          <button
            type="button"
            class="scrollControlBar__scopeChip scrollControlBar__scopeChip--clear"
            @click.stop="$emit('clear-selection')"
          >
            清除
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ControlBarCarousel from './ControlBarCarousel.vue'
import { CONTROL_BAR_BROADCAST } from './controlBarBroadcast/broadcastConfig'
import {
  getActiveBroadcastSlides,
  shouldRunBroadcast,
  shouldUseCarousel,
} from './controlBarBroadcast/broadcastHelper'

/**
 * 控制条中间「显示屏」：只编排 info / actions / split 布局；
 * idle 且无筛选时从 broadcastConfig 轮播 scope 默认帧与版本说明；有筛选/勾选时停播。
 */
export default {
  name: 'ControlBarDisplay',
  components: {
    ControlBarCarousel,
  },
  props: {
    layout: {
      type: String,
      default: 'info',
      validator(value) {
        return ['info', 'split', 'actions-full'].includes(value)
      },
    },
    /** 滚动态拆栏时只渲染 info 或 actions 一侧 */
    part: {
      type: String,
      default: null,
      validator(value) {
        return value == null || value === 'info' || value === 'actions'
      },
    },
    scopeFilters: {
      type: Array,
      default: () => [],
    },
    hasActiveScopeFilters: {
      type: Boolean,
      default: false,
    },
    scopeFilterTooltip: {
      type: String,
      default: '',
    },
    selectionCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    broadcastSlides() {
      return getActiveBroadcastSlides(CONTROL_BAR_BROADCAST)
    },
    broadcastPauseOnHover() {
      return CONTROL_BAR_BROADCAST.pauseOnHover !== false
    },
    /** idle 且无筛选：启用轮播（配置里至少有一条 release-note） */
    useBroadcastCarousel() {
      if (!shouldRunBroadcast({
        hasActiveScopeFilters: this.hasActiveScopeFilters,
        hasRowSelection: this.selectionCount > 0,
      })) {
        return false
      }
      return shouldUseCarousel(this.broadcastSlides)
    },
    scopeDefaultLabel() {
      const scopeSlide = this.broadcastSlides.find((s) => s.type === 'scope-default')
      return (scopeSlide && scopeSlide.label) || '全部资源'
    },
    rootClass() {
      return {
        'control-bar-display--split': this.layout === 'split' && this.part == null,
        'control-bar-display--actionsFull': this.layout === 'actions-full' && this.part == null,
      }
    },
    showInfoPart() {
      if (this.layout === 'info') {
        return this.part == null || this.part === 'info'
      }
      if (this.layout === 'split') {
        return this.part == null || this.part === 'info'
      }
      return false
    },
    showActionsPart() {
      if (this.layout === 'split' || this.layout === 'actions-full') {
        return this.part == null || this.part === 'actions'
      }
      return false
    },
  },
  methods: {
    /** 与 list.vue 原版一致：1/2/3 维筛选时控制 chip 等分宽度 */
    scopeChipsCountClass(count) {
      if (count === 1) {
        return 'scrollControlBar__scopeChips--count-1'
      }
      if (count === 2) {
        return 'scrollControlBar__scopeChips--count-2'
      }
      return 'scrollControlBar__scopeChips--count-3'
    },
  },
}
</script>

<style scoped>
.control-bar-display {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: var(--link-display-height, 36px);
}

.control-bar-display--split {
  gap: 8px;
}

.control-bar-display--split .scrollControlBar__summaryScope:first-child {
  flex: 1.1 1 0;
  min-width: 0;
}

.control-bar-display--split .scrollControlBar__summaryScope:last-child {
  flex: 0.9 1 0;
  min-width: 0;
}

.control-bar-display--actionsFull .scrollControlBar__summaryScope {
  flex: 1 1 100%;
}
</style>
